"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCodeBlockOptionsState = exports.CodeBlockOptionsStateProvider = exports.CodeBlockOptionsState = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const ui_1 = require("@jsonjoy.com/ui");
const slate_1 = require("slate");
const slate_react_1 = require("slate-react");
const settings = tslib_1.__importStar(require("./settings"));
const getElementSignature = (element) => JSON.stringify([
    element.language ?? '',
    element.fileName ?? '',
    element.wrap ?? null,
    element.showLineNumbers ?? null,
]);
const CodeBlockOptionsStateContext = React.createContext(null);
class CodeBlockOptionsState {
    constructor(editor, element, closePopup) {
        this.editor = editor;
        this.fileName = ui_1.rsync.val('');
        this.language = ui_1.rsync.val('');
        this.wrapColumn = ui_1.rsync.val(0);
        this.showLineNumbers = ui_1.rsync.val(true);
        this.signature = '';
        this.setElement = (element) => {
            const signature = getElementSignature(element);
            this.element = element;
            if (signature === this.signature)
                return;
            this.syncFromElement(element, signature);
        };
        this.setClosePopup = (closePopup) => {
            this.closePopup = closePopup;
        };
        this.setFileName = (value) => {
            this.fileName.set(value);
        };
        this.setLanguage = (value) => {
            this.language.set(value);
        };
        this.setWrapColumn = (value) => {
            this.wrapColumn.set(settings.getCodeBlockWrapColumn(value));
        };
        this.toggleShowLineNumbers = () => {
            this.showLineNumbers.set(!this.showLineNumbers.value);
        };
        this.inferLanguageFromFileName = () => {
            if (this.language.value.trim() !== '')
                return;
            const extension = this.fileName.value.split('.').pop()?.trim() || '';
            if (extension && extension !== this.language.value.trim())
                this.language.set(extension);
        };
        this.cancel = () => {
            this.syncFromElement(this.element);
            this.closePopup?.();
        };
        this.apply = () => {
            const nextLanguage = this.language.value.trim();
            const nextFileName = this.fileName.value.trim();
            const nextWrapColumn = settings.getStoredCodeBlockWrapColumn(this.wrapColumn.value);
            const nextShowLineNumbers = settings.getStoredCodeBlockShowLineNumbers(this.showLineNumbers.value);
            this.applyMetaValue('language', nextLanguage, this.element.language);
            this.applyMetaValue('fileName', nextFileName, this.element.fileName);
            this.applyNumberMetaValue('wrap', nextWrapColumn, this.element.wrap);
            this.applyBooleanMetaValue('showLineNumbers', nextShowLineNumbers, this.element.showLineNumbers);
            this.removeDeprecatedMetaValues();
            this.fileName.set(nextFileName);
            this.language.set(nextLanguage);
            this.wrapColumn.set(settings.getCodeBlockWrapColumn(nextWrapColumn));
            this.showLineNumbers.set(settings.getCodeBlockShowLineNumbers(nextShowLineNumbers));
            this.closePopup?.();
        };
        this.dispose = () => { };
        this.syncFromElement = (element, signature = getElementSignature(element)) => {
            this.signature = signature;
            this.fileName.set(element.fileName ?? '');
            this.language.set(element.language ?? '');
            this.wrapColumn.set(settings.getCodeBlockWrapColumn(element.wrap));
            this.showLineNumbers.set(settings.getCodeBlockShowLineNumbers(element.showLineNumbers));
        };
        this.applyMetaValue = (field, value, currentValue) => {
            const nextValue = value.trim();
            const prevValue = currentValue?.trim() ?? '';
            if (nextValue === prevValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (nextValue) {
                slate_1.Transforms.setNodes(this.editor, { [field]: nextValue }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.applyNumberMetaValue = (field, value, currentValue) => {
            if (value === currentValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value !== undefined) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.applyBooleanMetaValue = (field, value, currentValue) => {
            if (value === currentValue)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            if (value !== undefined) {
                slate_1.Transforms.setNodes(this.editor, { [field]: value }, { at: path });
            }
            else {
                slate_1.Transforms.unsetNodes(this.editor, field, { at: path });
            }
        };
        this.removeDeprecatedMetaValues = () => {
            const legacyElement = this.element;
            if (legacyElement.scrollAfterLines === undefined && legacyElement.previewLines === undefined)
                return;
            const path = slate_react_1.ReactEditor.findPath(this.editor, this.element);
            slate_1.Transforms.unsetNodes(this.editor, ['scrollAfterLines', 'previewLines'], { at: path });
        };
        this.element = element;
        this.closePopup = closePopup;
        this.syncFromElement(element);
    }
}
exports.CodeBlockOptionsState = CodeBlockOptionsState;
const CodeBlockOptionsStateProvider = ({ editor, element, closePopup, children, }) => {
    const stateRef = React.useRef(null);
    if (!stateRef.current)
        stateRef.current = new CodeBlockOptionsState(editor, element, closePopup);
    const state = stateRef.current;
    React.useEffect(() => {
        state.setClosePopup(closePopup);
    }, [closePopup, state]);
    React.useEffect(() => {
        state.setElement(element);
    }, [element, state]);
    React.useEffect(() => {
        return () => state.dispose();
    }, [state]);
    return React.createElement(CodeBlockOptionsStateContext.Provider, { value: state }, children);
};
exports.CodeBlockOptionsStateProvider = CodeBlockOptionsStateProvider;
const useCodeBlockOptionsState = () => {
    const state = React.useContext(CodeBlockOptionsStateContext);
    if (!state)
        throw new Error('CodeBlockOptionsStateContext is not available.');
    return state;
};
exports.useCodeBlockOptionsState = useCodeBlockOptionsState;
